import { TerritoryFallbackColor, territoryPaletteColor } from './territory-colors';
import * as L from 'leaflet';
import { SnapRadiusPx, pullOutOf, snapPoint } from './boundary-snapping';
import {
  EditableShape, VertexLink, cloneRings, linkSharedVertices, ringsDiffer,
  sharedSegmentInsertions, vertexKey,
} from './shared-boundary';

/**
 * Direct manipulation of a boundary on the persistent workspace map.
 *
 * Started explicitly by TerritoryFeature after the user chooses Edit.
 * Selection alone never creates handles or changes the boundary.
 *
 *   drag a node            move that corner
 *   drag anywhere on edge  a node appears there and follows the cursor
 *   right-click a node     remove it
 *
 * Grabbing an edge works along its whole length rather than only at a midpoint.
 * A midpoint handle asks you to hit one particular spot to add a corner; an
 * edge you can take hold of anywhere asks nothing of you. It is a fat
 * transparent line laid over the outline, because a 3px stroke is not a target
 * a mouse can be asked to hit.
 *
 * The map keeps its own gestures: nothing here binds a drag to the fill, so
 * panning still works inside a patch - panning across your own territory is not
 * an edit. Everything it does bind is unbound in stop(); a leaked handler on the
 * shared workspace map would follow the user to every other page.
 */
export class TerritoryBoundaryEditor {
  /**
   * Every shape this edit can move: the patch that was selected, plus each
   * neighbour that shares a corner with it.
   *
   * The neighbours are here because a shared border belongs to both sides. Move
   * a corner in one and leave the other, and the border comes apart - overlap
   * on one side of the drag, nobody's land on the other. Overlap is repaired on
   * save; the gap never was, because a hole between two territories is not
   * invalid, only wrong.
   */
  private shapes: EditableShape[] = [];
  /** Index into shapes of the one wearing the handles. */
  private primary = 0;
  /** Where each of the primary's corners also lives. Rebuilt whenever indices
   *  shift, or a link would address the corner next door to the right one. */
  private links = new Map<string, VertexLink[]>();
  /** Snapshots for undo, of every shape at once - an edit that moved two
   *  borders has to undo both or it leaves one of them halfway. */
  private history: EditableShape[][] = [];
  /** The shapes as they arrived, so the page can tell a real change from a
   *  click and stage only what moved. */
  private original: EditableShape[] = [];

  private readonly handleLayer = L.layerGroup();
  private readonly shapeLayer = L.layerGroup();
  private color = TerritoryFallbackColor;
  private attached = false;
  /** True between grabbing something and letting go, so the shape can say so. */
  private dragging = false;
  private cleanupEdgeDrag?: () => void;
  private draggedMarker?: L.Marker;
  private readonly onVertexBlur = () => {
    const marker = this.draggedMarker;
    if (!marker) return;
    // Leaflet owns vertex dragging, including document mouseup. Use its public
    // handler to release a drag whose mouseup was lost with window focus.
    marker.dragging?.disable();
    marker.fire('dragend');
  };

  /** Only the handles in view are built. A county-sized outline carries
   *  hundreds of corners, and a marker for every one makes the map crawl at
   *  exactly the zoom where you want to work carefully. */
  private readonly onMapMove = () => this.renderHandles();

  constructor(
    private readonly map: L.Map,
    /** Called after every committed change, so the page can show the area and
     *  enable its Apply without polling. */
    private readonly onChange: (rings: L.LatLng[][]) => void,
    /**
     * Called with the id of every territory this editor is DRAWING, whenever
     * that set changes: the patch being edited, and any neighbour that turns
     * out to share a corner with it and therefore moves with it.
     *
     * The page hides those territories' saved outlines. Without it the map
     * draws a moving neighbour twice - the editor's version following the
     * drag, and the map layer's saved one standing where the border used to
     * be - which is the old line the whole gesture is trying to move.
     */
    private readonly onShapesDrawn?: (territoryIds: readonly number[]) => void,
  ) {}

  get active(): boolean { return this.attached; }
  get canUndo(): boolean { return this.history.length > 0; }
  /** The outer ring's corner count - what the toolbar shows. */
  get vertexCount(): number { return this.rings[0]?.length ?? 0; }

  /** The selected patch's rings. */
  private get rings(): L.LatLng[][] { return this.shapes[this.primary]?.rings ?? []; }

  /**
   * Every shape this edit actually moved, the neighbours included. The page
   * stages one redraw per entry: a shared border that moved is a change to both
   * territories, and saving only the one under the cursor would put the gap
   * back.
   */
  get changedShapes(): { id: number; rings: L.LatLng[][] }[] {
    return this.shapes
      .filter((shape, i) => ringsDiffer(shape.rings, this.original[i]?.rings ?? []))
      .map(shape => ({ id: shape.id, rings: cloneRings(shape.rings) }));
  }

  /** How many neighbours would move with the selected patch. The panel says so,
   *  because a drag that reshapes two territories should not be a surprise. */
  get linkedNeighbourCount(): number {
    const touched = new Set<number>();
    this.links.forEach(list => list.forEach(l => touched.add(l.shape)));
    return touched.size;
  }

  /** True once anything differs from what opened, so a page can offer Apply for
   *  a real change and not for a stray click. */
  get changed(): boolean { return this.changedShapes.length > 0; }

  /** The selected patch's shape, cloned so a caller cannot mutate the
   *  editor's state. */
  get currentRings(): L.LatLng[][] { return cloneRings(this.rings); }

  /**
   * @param shapes  the patch being edited first, then every other patch on the
   *                map. Neighbours that turn out to share a corner move with it;
   *                the rest are only there to snap to.
   *
   * There is no separate list of snap targets. There used to be, built once
   * from the saved outlines, and it was wrong in two ways at the same time: it
   * never moved, so every drag after the first snapped against where the
   * neighbours had been rather than where they now were; and it contained the
   * neighbour's copy of a SHARED corner, so picking one up snapped it straight
   * back onto its own reflection and it could not be moved at all. Targets are
   * now read off these shapes as they stand.
   */
  start(
    shapes: readonly { id: number; rings: readonly (readonly L.LatLng[])[] }[],
    color: string,
  ): void {
    if (this.attached) this.stop();
    this.shapes = shapes.map(shape => ({ id: shape.id, rings: cloneRings(shape.rings) }));
    this.primary = 0;
    this.original = this.snapshot();
    this.links = linkSharedVertices(this.shapes, this.primary);
    this.color = territoryPaletteColor(color);
    this.history = [];

    if (!this.attached) {
      this.map.addLayer(this.shapeLayer);
      this.map.addLayer(this.handleLayer);
      this.map.on('moveend zoomend', this.onMapMove);
      this.attached = true;
    }
    this.render();
  }

  /** Give the map back. Safe when not editing and safe twice - a page tears
   *  down on route change without knowing whether an edit was live. */
  stop(): void {
    this.cleanupEdgeDrag?.();
    window.removeEventListener('blur', this.onVertexBlur);
    this.draggedMarker?.dragging?.disable();
    this.draggedMarker = undefined;
    this.dragging = false;
    if (this.attached) {
      this.attached = false;
      this.map.off('moveend zoomend', this.onMapMove);
      this.map.removeLayer(this.handleLayer);
      this.map.removeLayer(this.shapeLayer);
    }
    this.handleLayer.clearLayers();
    this.shapeLayer.clearLayers();
    this.shapes = [];
    this.original = [];
    this.links.clear();
    this.history = [];
    if (this.drawnKey !== '') {
      this.drawnKey = '';
      this.onShapesDrawn?.([]);
    }
  }

  undo(): void {
    const previous = this.history.pop();
    if (!previous) return;
    this.shapes = previous;
    this.links = linkSharedVertices(this.shapes, this.primary);
    this.render();
    this.onChange(this.currentRings);
  }

  private snapshot(): EditableShape[] {
    return this.shapes.map(shape => ({ id: shape.id, rings: cloneRings(shape.rings) }));
  }

  // ── Editing ─────────────────────────────────────────────────────────────

  private remember(): void {
    this.history.push(this.snapshot());
    // An edit is tens of moves; keeping every one of a long afternoon would
    // hold the whole outline in memory dozens of times over.
    if (this.history.length > 100) this.history.shift();
  }

  /**
   * Moves a corner - and the same corner wherever else it lives.
   *
   * This is the whole point of the neighbours being loaded. One coordinate is
   * written to every shape holding it, so the two patches keep sharing the
   * literal same point: one grows by exactly what the other gives up, with no
   * gap and no overlap in between, and no repair needed on save.
   */
  private moveVertex(ringIndex: number, index: number, to: L.LatLng): void {
    const at = this.snap(to, ringIndex, index);
    this.rings[ringIndex][index] = at;

    for (const link of this.links.get(vertexKey(ringIndex, index)) ?? []) {
      const ring = this.shapes[link.shape]?.rings[link.ring];
      if (ring && link.index < ring.length) ring[link.index] = L.latLng(at.lat, at.lng);
    }
    this.renderShape();
  }

  /**
   * Removes a corner, unless the ring is down to a triangle. Below three the
   * shape encloses nothing, and a boundary that encloses nothing owns no
   * prospects - which reads on the map as a territory that emptied itself.
   */
  private removeVertex(ringIndex: number, index: number): void {
    if (this.rings[ringIndex].length <= 3) return;

    // Refused rather than half-done: dropping a shared corner from one side
    // only would leave the neighbour with a corner on a line that no longer
    // passes through it, which is the gap this class exists to prevent. A
    // neighbour already down to a triangle cannot give the corner up.
    const shared = this.links.get(vertexKey(ringIndex, index)) ?? [];
    if (shared.some(l => (this.shapes[l.shape]?.rings[l.ring]?.length ?? 0) <= 3)) return;

    this.remember();
    this.rings[ringIndex].splice(index, 1);
    // Descending, so an earlier removal cannot shift a later index.
    [...shared]
      .sort((a, b) => b.index - a.index)
      .forEach(l => this.shapes[l.shape]?.rings[l.ring]?.splice(l.index, 1));

    this.links = linkSharedVertices(this.shapes, this.primary);
    this.render();
    this.onChange(this.currentRings);
  }

  /**
   * Snaps to a neighbour, and to this ring's own other corners so a shape can
   * be closed cleanly onto itself. The corner being dragged is excluded, or it
   * would snap to where it already is and never move.
   *
   * Failing to find anything within reach is not the end of it. A corner dragged
   * well INSIDE a neighbour is near none of its edges, so proximity has nothing
   * to say - and that is exactly the drag that makes two patches overlap. It is
   * put back on the edge it crossed, which means a corner cannot be dragged into
   * a neighbour at all: it slides along the border instead. Only neighbours
   * count here; being inside one's own ring is ordinary.
   */

  /**
   * How close, in screen pixels, two boundaries have to be before this treats
   * them as the same line. Deliberately tight: this is "the user sees one
   * border here", not "these are roughly near each other".
   */
  private static readonly CoincidentPx = 2;

  /**
   * Give a corner about to be dragged something on each neighbour to drag WITH.
   *
   * Coupling only ever worked for corners the two shapes already held in
   * common, to the last bit. Real borders are rarely like that. Two patches
   * meeting along a street may carry a different number of corners on the same
   * line - so a corner of this one sits on an EDGE of that one, with nothing
   * to link to - or may have been drawn, planned or trimmed independently and
   * agree only to within a pixel. In both cases the drag moved one side of the
   * border and left the other where it was, which is a gap on one side of the
   * cursor and an overlap on the other.
   *
   * So the two are made to agree first, at the corner's CURRENT position, and
   * only then is anything dragged:
   *
   *   on a neighbour's edge   the neighbour gains a corner there
   *   a hair off its corner   this corner takes the neighbour's exact one
   *
   * Both leave the border where it already looked, so nothing visibly moves;
   * what changes is that there is now a link, and the drag carries both sides.
   */
  private coupleToNeighbours(ringIndex: number, index: number): void {
    const ring = this.rings[ringIndex];
    const point = ring?.[index];
    if (!point) return;

    const linked = new Set(
      (this.links.get(vertexKey(ringIndex, index)) ?? []).map(l => `${l.shape}:${l.ring}`));
    let coupled = false;

    this.shapes.forEach((shape, s) => {
      if (s === this.primary) return;
      shape.rings.forEach((other, r) => {
        if (linked.has(`${s}:${r}`)) return;
        const found = snapPoint(
          point, this.map, [other], TerritoryBoundaryEditor.CoincidentPx);
        if (!found) return;

        if (found.kind === 'vertex') {
          // Meant to be the same corner and missed by a hair. Take theirs
          // rather than moving it: the neighbour is not the shape being edited.
          ring[index] = L.latLng(found.latlng.lat, found.latlng.lng);
        } else {
          other.splice(Math.floor(found.at) + 1, 0, L.latLng(point.lat, point.lng));
        }
        coupled = true;
      });
    });

    if (coupled) this.links = linkSharedVertices(this.shapes, this.primary);
  }

  private snap(point: L.LatLng, ringIndex: number, exceptIndex: number): L.LatLng {
    const own = this.rings.map((ring, r) =>
      r === ringIndex ? ring.filter((_, i) => i !== exceptIndex) : ring);
    const found = snapPoint(
      point, this.map, [...this.attractors(ringIndex, exceptIndex), ...own], SnapRadiusPx);
    if (found) return found.latlng;
    return pullOutOf(point, this.map, this.noGoZones(ringIndex, exceptIndex)) ?? point;
  }

  /**
   * The patches this corner may NOT be dragged into.
   *
   * Every other patch, except the ones this corner is shared with. A shared
   * corner pushed into its neighbour is not an incursion: the neighbour's
   * border is that same corner and moves with it, so the two stay met and one
   * simply gives up the ground the other takes. Treating those as out of
   * bounds is what stopped a border between two territories being moved at
   * all - every drag toward the neighbour was shoved back onto the line it
   * started on.
   *
   * Whole rings, unlike the attractors: this asks whether a point is INSIDE a
   * shape, and a ring with a corner removed is a different shape to be inside
   * of.
   */
  private noGoZones(ringIndex: number, exceptIndex: number): L.LatLng[][] {
    const shared = new Set((exceptIndex >= 0
      ? this.links.get(vertexKey(ringIndex, exceptIndex)) ?? []
      : []).map(link => link.shape));
    const zones: L.LatLng[][] = [];
    this.shapes.forEach((shape, s) => {
      if (s === this.primary || shared.has(s)) return;
      shape.rings.forEach(ring => zones.push(ring));
    });
    return zones;
  }

  /**
   * The other patches as they stand right now, minus the copies of the corner
   * being dragged.
   *
   * Both halves matter. Reading them live is what lets a neighbour that has
   * already moved with this drag be snapped against where it IS. Dropping the
   * linked copies is what lets a shared corner move at all: it exists in this
   * ring and in the neighbour's, so leaving the neighbour's in would have the
   * point snapping onto itself the instant it was picked up - which is exactly
   * how a border between two territories came to refuse to be dragged.
   */
  private attractors(ringIndex: number, exceptIndex: number): L.LatLng[][] {
    const linked = exceptIndex >= 0
      ? this.links.get(vertexKey(ringIndex, exceptIndex)) ?? []
      : [];
    const targets: L.LatLng[][] = [];
    this.shapes.forEach((shape, s) => {
      if (s === this.primary) return;
      shape.rings.forEach((ring, r) => {
        targets.push(ring.filter((_, i) =>
          !linked.some(link => link.shape === s && link.ring === r && link.index === i)));
      });
    });
    return targets;
  }

  // ── Rendering ───────────────────────────────────────────────────────────

  private render(): void {
    this.renderShape();
    this.renderHandles();
  }

  private renderShape(): void {
    this.shapeLayer.clearLayers();
    if (this.rings.length === 0) return;

    // Neighbours that move with this edit are drawn too, and drawn as being in
    // the edit: a border shifting while the patch on the other side appears not
    // to change would read as the map coming apart.
    this.shapes.forEach((shape, i) => {
      if (i === this.primary || !shape.rings.length) return;
      if (!this.movesWith(i)) return;
      this.shapeLayer.addLayer(L.polygon(shape.rings, {
        color: '#5e35b1',
        weight: 2,
        fillOpacity: 0.1,
        dashArray: '4,4',
        className: 'tr-editing tr-editing-neighbour',
        interactive: false,
      }));
    });

    this.shapeLayer.addLayer(L.polygon(this.rings, {
      color: this.color,
      fillColor: this.color,
      weight: 3,
      fillOpacity: this.dragging ? 0.26 : 0.18,
      className: this.dragging
        ? 'tr-editing tr-editing-live map-area-selected'
        : 'tr-editing map-area-selected',
      // Not interactive: a handler on the fill would swallow the drag that pans
      // the map, and panning across your own patch is not an edit.
      interactive: false,
    }));

    this.announceDrawn();

    // The grab target for the outline. Transparent and fat, which is what makes
    // "drag the edge" work anywhere along it instead of at one blessed midpoint.
    this.rings.forEach((ring, ringIndex) => {
      this.shapeLayer.addLayer(L.polyline([...ring, ring[0]], {
        color: this.color,
        weight: 14,
        opacity: 0,
        className: 'tr-edge-grab',
        interactive: true,
      }).on('mousedown', (e: L.LeafletMouseEvent) => this.beginEdgeDrag(ringIndex, e)));
    });
  }

  /** True when this shape shares at least one corner with the one being
   *  edited, and so will move with it. */
  /**
   * Which territories are on screen because of this edit.
   *
   * A neighbour joins the moment a corner couples to it, which happens on
   * dragstart - so this is announced from renderShape, which runs then and on
   * every move after it, rather than from the committed-change callback that
   * fires only when the gesture is over.
   */
  private announceDrawn(): void {
    if (!this.onShapesDrawn) return;
    const ids = this.shapes
      .filter((_, i) => i === this.primary || this.movesWith(i))
      .map(shape => shape.id);
    const key = ids.join(',');
    if (key === this.drawnKey) return;
    this.drawnKey = key;
    this.onShapesDrawn(ids);
  }

  /** What was last announced, so a redraw per mouse-move is not a callback per
   *  mouse-move: the set changes when a neighbour couples, not when a corner
   *  moves a pixel. */
  private drawnKey = '';

  private movesWith(shapeIndex: number): boolean {
    for (const list of this.links.values()) {
      if (list.some(l => l.shape === shapeIndex)) return true;
    }
    return false;
  }

  /** A handle per visible corner. Shared ones look different, because dragging
   *  one reshapes two territories and that should be visible before the drag,
   *  not discovered during it. */
  private renderHandles(): void {
    this.handleLayer.clearLayers();
    if (!this.attached || this.rings.length === 0) return;

    const bounds = this.map.getBounds().pad(0.25);
    this.rings.forEach((ring, ringIndex) => {
      ring.forEach((point, index) => {
        if (bounds.contains(point)) {
          this.handleLayer.addLayer(this.vertexHandle(ringIndex, index, point));
        }
      });
    });
  }

  private vertexHandle(ringIndex: number, index: number, at: L.LatLng): L.Marker {
    const marker = L.marker(at, {
      draggable: true,
      keyboard: false,
      icon: L.divIcon({
        className: (this.links.get(vertexKey(ringIndex, index))?.length ?? 0) > 0
          ? 'tr-vertex-handle tr-vertex-shared'
          : 'tr-vertex-handle',
        iconSize: [18, 18],
      }),
      zIndexOffset: 1000,
    });

    marker.on('dragstart', () => {
      if (!this.attached) return;
      this.draggedMarker = marker;
      window.addEventListener('blur', this.onVertexBlur);
      this.remember();
      // Before anything moves, make sure the neighbours have something to move
      // WITH. A corner sitting on a neighbour's edge, or a hair off its own
      // corner, has nothing linked to it, and the drag would take one side of
      // the border away from the other.
      this.coupleToNeighbours(ringIndex, index);
      // The corner under the cursor and the edges it is bending stand out while
      // it moves, so what is changing is never in doubt mid-gesture.
      marker.getElement()?.classList.add('tr-handle-active');
      this.dragging = true;
      this.renderShape();
    });
    marker.on('drag', e => {
      if (this.attached) this.moveVertex(ringIndex, index, (e.target as L.Marker).getLatLng());
    });
    marker.on('dragend', () => {
      window.removeEventListener('blur', this.onVertexBlur);
      this.draggedMarker = undefined;
      if (!this.attached) return;
      marker.getElement()?.classList.remove('tr-handle-active');
      this.dragging = false;
      // The snapped position is authoritative; the marker can sit a few pixels
      // off it after the pointer is released.
      marker.setLatLng(this.rings[ringIndex][index]);
      // The SHAPE too, not only the handles: it is carrying the thickened
      // mid-drag stroke, which ends with the gesture. Redrawing handles alone
      // left that styling on screen until something unrelated redrew.
      this.render();
      this.onChange(this.currentRings);
    });
    marker.on('contextmenu', e => {
      L.DomEvent.stop(e);
      this.removeVertex(ringIndex, index);
    });
    marker.on('click', e => {
      // Alt-click for anyone without a right button, and for trackpads where
      // the context menu is a gesture people do not think to try.
      if ((e.originalEvent as MouseEvent).altKey) {
        L.DomEvent.stop(e);
        this.removeVertex(ringIndex, index);
      }
    });

    return marker;
  }

  /**
   * Taking hold of an edge. A corner is created where the edge was grabbed and
   * then follows the cursor, so the whole thing is one continuous drag rather
   * than "add a point, let go, find it, drag it".
   */
  private beginEdgeDrag(ringIndex: number, start: L.LeafletMouseEvent): void {
    if (!this.attached || this.dragging) return;
    L.DomEvent.stop(start);
    const ring = this.rings[ringIndex];
    const segment = this.nearestSegment(ring, start.latlng);
    if (segment < 0) return;

    this.remember();
    const a = ring[segment];
    const b = ring[(segment + 1) % ring.length];
    // Found BEFORE the insert, while the two endpoints still describe one
    // segment on each side. A corner added to only one of them would put a bend
    // in one border and leave the other straight - the gap, opened by hand.
    const alsoIn = sharedSegmentInsertions(this.shapes, this.primary, a, b);

    const at = this.snap(start.latlng, ringIndex, -1);
    const index = segment + 1;
    ring.splice(index, 0, at);
    for (const insertion of alsoIn) {
      this.shapes[insertion.shape]?.rings[insertion.ring]
        ?.splice(insertion.index, 0, L.latLng(at.lat, at.lng));
    }
    // Indices after the insert have all shifted.
    this.links = linkSharedVertices(this.shapes, this.primary);
    this.render();

    // The map must not pan under the drag; it is handed back the moment the
    // pointer is released, including when the release happens off the shape.
    this.map.dragging.disable();
    this.dragging = true;
    this.renderShape();
    const move = (e: L.LeafletMouseEvent) => this.moveVertex(ringIndex, index, e.latlng);
    const end = (event?: Event | L.LeafletEvent) => {
      if (event instanceof MouseEvent && event.button !== 0) return;
      if (!this.cleanupEdgeDrag) return;
      this.cleanupEdgeDrag();
      this.render();
      this.onChange(this.currentRings);
    };
    this.cleanupEdgeDrag = () => {
      this.cleanupEdgeDrag = undefined;
      this.dragging = false;
      this.map.off('mousemove', move);
      this.map.off('mouseup', end);
      document.removeEventListener('mouseup', end, true);
      window.removeEventListener('blur', end);
      this.map.dragging.enable();
    };
    this.map.on('mousemove', move);
    this.map.on('mouseup', end);
    document.addEventListener('mouseup', end, true);
    window.addEventListener('blur', end);
  }

  /** Index of the segment starting at the returned position, or -1. Measured in
   *  screen pixels, so the nearest edge is the one that looks nearest. */
  private nearestSegment(ring: readonly L.LatLng[], at: L.LatLng): number {
    if (ring.length < 2) return -1;
    const p = this.map.latLngToLayerPoint(at);
    let best = -1;
    let bestDistance = Infinity;

    for (let i = 0; i < ring.length; i++) {
      const a = this.map.latLngToLayerPoint(ring[i]);
      const b = this.map.latLngToLayerPoint(ring[(i + 1) % ring.length]);
      const distance = distanceToSegment(p, a, b);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    return best;
  }
}

function distanceToSegment(p: L.Point, a: L.Point, b: L.Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return p.distanceTo(a);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return p.distanceTo(L.point(a.x + t * dx, a.y + t * dy));
}
